import { getDecodedToken } from '../../service/service';
import { isLocalEnv } from '../../service/config';
import { Message } from './PepWiseAIChatPopup.types';

// ─────────────────────────────────────────────────────────────────────────────
// Pure helpers — defined OUTSIDE the component so they never re-create
// ─────────────────────────────────────────────────────────────────────────────

/** Resolve the current user's ID from the JWT cookie. Called once at mount. */
export function resolveUserId(): string {
    const decoded: any = getDecodedToken();
    const isLocalHost =
        typeof window !== 'undefined' &&
        (window.location.hostname === 'localhost' ||
            window.location.hostname === '127.0.0.1');
    return (
        decoded?.email ||
        decoded?.upn ||
        decoded?.sub ||
        (isLocalEnv || isLocalHost ? 'local-demo-user' : 'anonymous')
    );
}

export function getSecureRandom(): number {
    if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
        const array = new Uint32Array(1);
        window.crypto.getRandomValues(array);
        return array[0] / (0xffffffff + 1);
    }
    const time = Date.now() + (typeof performance === 'undefined' ? 0 : performance.now());
    return (time % 1e9) / 1e9;
}

/** Generate a collision-resistant session ID. */
export function newSessionId(): string {
    return `sess-${Date.now()}-${getSecureRandom().toString(36).slice(2, 8)}`;
}

/** Storage key for the active session of a user. */
export const sessionKey = (uid: string) => `pepwise_session_${uid}`;

/** Storage key for the cached messages of a session. */
export const msgCacheKey = (sessionId: string) => `pepwise_msgs_${sessionId}`;

/** Read or create the persisted sessionId for this user. */
export function getOrCreateSessionId(uid: string): string {
    const key = sessionKey(uid);
    const existing = localStorage.getItem(key);
    if (existing) return existing;
    const fresh = newSessionId();
    localStorage.setItem(key, fresh);
    return fresh;
}

/** Read the cached messages for this session from sessionStorage. */
export function readCachedMessages(sessionId: string): Message[] | null {
    try {
        const raw = sessionStorage.getItem(msgCacheKey(sessionId));
        if (raw) return JSON.parse(raw);
    } catch {
        // ignore JSON parse errors
    }
    return null;
}

/** Wipe all AI chat state for this user from both storages. */
export function clearChatStorage(uid: string) {
    localStorage.removeItem(sessionKey(uid));
    sessionStorage.removeItem('pepwise_chat_isExpanded');
}

// ─────────────────────────────────────────────────────────────────────────────
// Format raw AI response text
// ─────────────────────────────────────────────────────────────────────────────

export function formatAssistantMessage(raw: string): string {
    let text = (raw || '').trim();
    if (!text) return 'No response returned.';

    if ((text.startsWith('"') && text.endsWith('"')) || (text.startsWith("'") && text.endsWith("'"))) {
        try { text = JSON.parse(text); } catch { text = text.slice(1, -1); }
    }

    // If the payload accidentally contains a JSON string object, extract the text
    try {
        const parsed = JSON.parse(text);
        if (parsed.text_response) text = parsed.text_response;
        else if (parsed.content) text = parsed.content;
        else if (parsed.answer) text = parsed.answer;
    } catch {
        // Ignore, it's just raw text
    }

    // Just fix basic escaped newlines. Do NOT strip markdown characters!
    // The UI will now render markdown natively.
    text = text.replaceAll(String.raw`\n`, '\n').trim();

    if (/^"[\s\S]*"$/.test(text)) {
        text = text.slice(1, -1).trim();
    }

    // If the ENTIRE message is wrapped in single backticks, strip them
    if (text.startsWith('`') && text.endsWith('`') && text.indexOf('`', 1) === text.length - 1) {
        text = text.slice(1, -1).trim();
    }

    // If this looks like a generic error response, strip all backticks so it doesn't get highlighted as code
    if (text.toLowerCase().includes('something went wrong') || text.toLowerCase().includes("i wasn't able to construct")) {
        text = text.replaceAll('`', '');
    }

    return text;
}

// ─────────────────────────────────────────────────────────────────────────────
// Markdown rendering
// ─────────────────────────────────────────────────────────────────────────────

export function renderMarkdownHTML(text: string): string {
    if (!text) return '';
    let html = text
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        // Headings
        .replace(/^### (.*$)/gim, '<h4 style="margin: 12px 0 6px 0; font-size: 14px; color: inherit;">$1</h4>')
        .replace(/^## (.*$)/gim, '<h3 style="margin: 14px 0 8px 0; font-size: 15px; color: inherit;">$1</h3>')
        .replace(/^# (.*$)/gim, '<h2 style="margin: 16px 0 10px 0; font-size: 16px; color: inherit;">$1</h2>')
        // Bold / Italic
        .replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/gim, '<em>$1</em>')
        // Inline code
        .replace(/`([^`]+)`/gim, '<code style="background: rgba(0,0,0,0.06); padding: 2px 4px; border-radius: 4px; font-family: monospace; font-size: 12px;">$1</code>')
        // Bullets (support - or * or •)
        .replace(/^[ \t]{0,3}[-*•][ \t]{1,4}([^\n]+)/gim, '<li style="margin-left: 20px; list-style-type: disc; margin-bottom: 4px;">$1</li>')
        // Numbered lists
        .replace(/^[ \t]{0,3}(\d+)\.[ \t]{1,4}([^\n]+)/gim, '<li style="margin-left: 20px; list-style-type: decimal; margin-bottom: 4px;">$2</li>')
        // Remove --- horizontal rules
        .replaceAll('---', '')
        // Line breaks
        .replace(/\n/gim, '<br />');

    // Clean up <br /> around lists and headings
    html = html.replaceAll(/(<\/li>)(<br \/>)+/g, '$1');
    html = html.replaceAll(/(<\/h[234]>)(<br \/>)+/g, '$1');
    html = html.replaceAll(/(<br \/>)+(<h[234]>)/g, '$2');

    // Collapse 3 or more consecutive <br /> into max 2 for normal paragraph spacing
    html = html.replaceAll(/(<br \/>){3,}/g, '<br /><br />');

    return html;
}
